/*
 * ResumenCliente.js — Pestaña Resumen de la ficha del cliente (C-023, ver diseno/CAMBIOS.md).
 *
 * Solo lectura: todo lo de un cliente en un lugar. Columna principal (el trabajo): este mes, el año
 * mes por mes, obligaciones adicionales y declaración anual. Columna lateral (el contexto): datos,
 * cobranza, notas y documentos recientes. Los estados se cambian en sus tablas ("Ver en la tabla →").
 *
 * props:
 *   cliente, procesos, fechasLimite, periodo { mes 1-12, anio }, ejercicio
 *   obligaciones    las del mes (C-021), ya filtradas a este cliente: [{ clave, nombre, toca, trimestral, fila }]
 *   api             { estadosAnio(id, anio), anual(ejercicio, id), cobranza(id) | null, documentos(id, anio), abrirArchivo(a) }
 *   verEnTabla      (vista) => {}   'mensual' | 'obligaciones' | 'anual' | 'cobranza'
 *   irDocumentos    () => {}
 *   notas           JSX de las notas (el mismo bloque de siempre, con su formulario)
 *   Icono, Sello    componentes del círculo y del sello (App.js)
 */
import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import {
  OPCIONES_REVISION, normalizarRevision, calcularPasoActual, esOpinionCumplimiento, estadoVisible, fechaCorta, pasosCascada, resultadoOpinion,
} from './estados';
import { MESES_CORTOS, resumenDelAnio } from './mesesCliente';
import { estadoObligacion, fechaLimiteObligacion, textoNoToca, textoPeriodo, OBLIGACIONES } from './obligaciones';
import { PASOS_ANUAL, fechaLimiteAnual, pasoVisibleAnual } from './anual';
import { formatoPesos, nombreMesAnio } from './cobranzaLogica';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const NOMBRE_OBLIGACION = { 'no-aplica': 'No aplica' };

function Bloque({ titulo, detalle, accion, children }) {
  return (
    <section className="res-bloque">
      <div className="res-cabecera">
        <h3>{titulo}</h3>
        {detalle && <span className="res-detalle">{detalle}</span>}
        {accion}
      </div>
      {children}
    </section>
  );
}

const IrTabla = ({ onClick, texto = 'Ver en la tabla →' }) => (
  <button type="button" className="res-ir" onClick={onClick}>{texto}</button>
);

export default function ResumenCliente({
  cliente, procesos, fechasLimite, periodo, ejercicio, obligaciones, api, verEnTabla, irDocumentos, notas, Icono, Sello,
}) {
  const [filasAnio, setFilasAnio] = useState(null);
  const [mesElegido, setMesElegido] = useState(periodo.mes);
  const [anual, setAnual] = useState(undefined); // undefined = cargando; null = no presenta
  const [cobranza, setCobranza] = useState(null);
  const [documentos, setDocumentos] = useState(null);
  const hoy = new Date();
  const esResico = cliente.regimen === 'resico';

  useEffect(() => { setMesElegido(periodo.mes); }, [periodo.mes, periodo.anio]);

  useEffect(() => {
    let vivo = true;
    setFilasAnio(null);
    api.estadosAnio(cliente.id, periodo.anio)
      .then(r => { if (vivo) setFilasAnio(r.estados || []); })
      .catch(() => { if (vivo) setFilasAnio([]); });
    api.documentos(cliente.id, periodo.anio)
      .then(r => { if (vivo) setDocumentos(r || []); })
      .catch(() => { if (vivo) setDocumentos([]); });
    return () => { vivo = false; };
  }, [api, cliente.id, periodo.anio]);

  useEffect(() => {
    let vivo = true;
    if (esResico) { setAnual(null); return undefined; }
    setAnual(undefined);
    api.anual(ejercicio, cliente.id)
      .then(r => { if (vivo) setAnual((r.clientes || [])[0] || null); })
      .catch(() => { if (vivo) setAnual(null); });
    return () => { vivo = false; };
  }, [api, cliente.id, ejercicio, esResico]);

  useEffect(() => {
    let vivo = true;
    if (!api.cobranza) { setCobranza(null); return undefined; }
    api.cobranza(cliente.id)
      .then(r => { if (vivo) setCobranza(r); })
      .catch(() => { if (vivo) setCobranza(null); });
    return () => { vivo = false; };
  }, [api, cliente.id]);

  // Este mes: para el mes de la barra se usan los estados vivos de la tabla; para otro mes, los del año.
  const esMesBarra = mesElegido === periodo.mes;
  const estadosMes = esMesBarra ? cliente.estados || [] : (filasAnio || []).filter(f => Number(f.mes) === mesElegido);
  const clienteMes = { ...cliente, estados: estadosMes };
  const periodoMes = { mes: mesElegido, anio: periodo.anio };
  const pasoActual = calcularPasoActual(clienteMes, procesos, fechasLimite, periodoMes, hoy);
  const cascada = pasosCascada(procesos);
  const procesoOpinion = procesos.find(esOpinionCumplimiento);
  const opinion = procesoOpinion
    ? resultadoOpinion((estadosMes.find(e => e.proceso_id === procesoOpinion.id) || {}).estado || 'rojo')
    : null;
  const pasoCorto = pasoActual ? cascada.find(p => p.proceso.id === pasoActual.proceso.id)?.corto : null;

  const anioMeses = filasAnio
    ? resumenDelAnio({ filas: filasAnio, procesos, fechasLimite, anio: periodo.anio, revision_pedro: cliente.revision_pedro }, hoy)
    : null;
  // El mes de la barra se cuenta con los estados vivos (por si se acaban de cambiar en la tabla).
  if (anioMeses) {
    const vivo = resumenDelAnio({ filas: (cliente.estados || []).map(e => ({ ...e, mes: periodo.mes })), procesos, fechasLimite, anio: periodo.anio, revision_pedro: cliente.revision_pedro }, hoy)[periodo.mes - 1];
    anioMeses[periodo.mes - 1] = vivo;
  }

  const revision = OPCIONES_REVISION.find(o => o.valor === normalizarRevision(cliente.revision_pedro));
  const debe = cobranza ? (cobranza.meses || []).filter(m => m.por_cobrar > 0 && m.estado !== 'presentado_cero') : [];
  const totalDebe = debe.reduce((s, m) => s + m.por_cobrar, 0);
  const ultimoPago = cobranza ? (cobranza.meses || []).flatMap(m => m.pagos || []).filter(p => p.fecha).sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0] : null;
  const honorarios = cobranza && cobranza.meses && cobranza.meses.length ? cobranza.meses[cobranza.meses.length - 1].honorarios : cliente.honorarios;

  return (
    <div className="resumen-cliente">
      <div className="res-principal">
        <Bloque
          titulo={`${esMesBarra ? 'Este mes' : 'Mes'} · ${MESES[mesElegido - 1]} ${periodo.anio}`}
          detalle={(
            <>
              {pasoActual ? <>Detenido en <b>{pasoCorto}</b>{pasoActual.diasAtraso > 0 && <span className="res-rojo"> · atrasado {pasoActual.diasAtraso} {pasoActual.diasAtraso === 1 ? 'día' : 'días'}</span>}</> : <b>Terminado</b>}
              {procesoOpinion && <> · Opinión: <span className={`opinion-pill ${opinion || 'sin-sacar'} leyenda`}>{opinion === 'positiva' ? 'Positiva' : opinion === 'negativa' ? 'Negativa' : 'Sin sacar'}</span></>}
            </>
          )}
          accion={<IrTabla onClick={() => verEnTabla('mensual', mesElegido)} />}
        >
          <ol className="res-pasos">
            {cascada.map(p => {
              const v = estadoVisible(clienteMes, p.proceso, { pasoActual, fechasLimite, periodo: periodoMes, hoy });
              return (
                <li key={p.proceso.id}>
                  <span className="estado-circulo-wrap">
                    <span className={`estado-circle ${v.clave}${v.atrasado ? ' atrasado' : ''}`} data-tooltip={v.tooltip} aria-label={`${p.corto}: ${v.tooltip}`} role="img">
                      <Icono clave={v.clave} size={14} />
                    </span>
                    <Sello tipo={v.sello} />
                  </span>
                  <span className="res-paso-nombre">{p.corto}</span>
                </li>
              );
            })}
          </ol>
        </Bloque>

        <Bloque titulo={`${periodo.anio}, mes por mes`} detalle="Un clic en un mes muestra sus pasos arriba">
          {anioMeses ? (
            <div className="res-meses">
              {anioMeses.map(m => (
                <button
                  key={m.mes}
                  type="button"
                  className={`res-mes${m.futuro ? ' futuro' : ''}${m.terminado ? ' terminado' : ''}${m.dias > 0 ? ' atrasado' : ''}${m.mes === mesElegido ? ' elegido' : ''}`}
                  disabled={m.futuro}
                  aria-pressed={m.mes === mesElegido}
                  data-tooltip={m.futuro ? 'Todavía no se trabaja' : m.terminado ? 'Terminado' : `Detenido en ${m.corto}${m.dias ? `, atrasado ${m.dias} días` : ''}`}
                  onClick={() => setMesElegido(m.mes)}
                >
                  <span className="res-mes-nombre">{MESES_CORTOS[m.mes - 1]}</span>
                  <span className="res-mes-estado">
                    {m.futuro ? '' : m.terminado ? '✓' : m.dias > 0 ? `+${m.dias}d` : m.corto}
                  </span>
                </button>
              ))}
            </div>
          ) : <p className="res-vacio">Cargando…</p>}
        </Bloque>

        {obligaciones.length > 0 && (
          <Bloque
            titulo="Obligaciones adicionales"
            detalle={`${MESES[periodo.mes - 1]} ${periodo.anio}`}
            accion={<IrTabla onClick={() => verEnTabla('obligaciones')} />}
          >
            <table className="res-mini">
              <tbody>
                {obligaciones.map(o => {
                  const def = OBLIGACIONES.find(x => x.clave === o.clave) || { nombre: o.nombre, trimestral: o.trimestral };
                  if (!o.toca) {
                    return <tr key={o.clave}><td className="res-mini-nombre">{def.nombre}<small>{textoNoToca(periodo, hoy)}</small></td><td colSpan={2} /></tr>;
                  }
                  const e = estadoObligacion(o.fila, periodo, hoy);
                  return (
                    <tr key={o.clave}>
                      <td className="res-mini-nombre">
                        {def.nombre}
                        <small>{textoPeriodo(def, periodo.mes)} · a más tardar {fechaCorta(fechaLimiteObligacion(periodo), hoy)}</small>
                      </td>
                      {[['envio', 'Envío'], ['pago', 'Pago']].map(([paso, texto]) => (
                        <td key={paso} className="res-mini-paso">
                          <span className="res-etq">{texto}</span>
                          <span className="estado-circulo-wrap">
                            <span className={`estado-circle ${e[paso]}${e.pasoActual === paso && e.atrasado ? ' atrasado' : ''}`} role="img" aria-label={`${texto}: ${e[paso]}`}>
                              <Icono clave={e[paso]} size={14} />
                            </span>
                            {e.pasoActual === paso && e.atrasado && <span className="atraso-badge" aria-hidden="true">+{e.dias}d</span>}
                          </span>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Bloque>
        )}

        {!esResico && anual !== null && (
          <Bloque
            titulo="Declaración anual"
            detalle={`ejercicio ${ejercicio} · a más tardar ${fechaCorta(fechaLimiteAnual(cliente.tipo, ejercicio), new Date(0))}`}
            accion={<IrTabla onClick={() => verEnTabla('anual')} />}
          >
            {anual === undefined ? <p className="res-vacio">Cargando…</p> : (
              <ol className="res-pasos">
                {PASOS_ANUAL.map(p => {
                  const v = pasoVisibleAnual(anual, p.clave, ejercicio, hoy);
                  return (
                    <li key={p.clave}>
                      {v.noAplica ? <span className="anual-no-aplica leyenda" data-tooltip={v.tooltip}>{NOMBRE_OBLIGACION['no-aplica']}</span> : (
                        <span className="estado-circulo-wrap">
                          <span className={`estado-circle ${v.clave}${v.atrasado ? ' atrasado' : ''}`} data-tooltip={v.tooltip} aria-label={`${p.nombre}: ${v.tooltip}`} role="img">
                            <Icono clave={v.clave} size={14} />
                          </span>
                          <Sello tipo={v.sello} />
                          {v.atrasado && <span className="atraso-badge" aria-hidden="true">+{v.dias}d</span>}
                        </span>
                      )}
                      <span className="res-paso-nombre">{p.nombre}{v.nota ? <small>{v.nota}</small> : null}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </Bloque>
        )}
      </div>

      <aside className="res-lateral">
        <Bloque titulo="Datos">
          <dl className="res-datos">
            <dt>Contador</dt><dd>{cliente.contador_nombre || 'Sin asignar'}</dd>
            <dt>Correo</dt><dd>{cliente.email || <span className="res-ambar">Sin correo</span>}</dd>
            <dt>Teléfono</dt><dd>{cliente.telefono || '—'}</dd>
            <dt>Revisión de Pedro</dt><dd>{revision ? revision.texto : 'No necesita'}</dd>
            {cliente.fecha_creacion && <><dt>Cliente desde</dt><dd>{fechaCorta(cliente.fecha_creacion, new Date(0))}</dd></>}
          </dl>
        </Bloque>

        {api.cobranza && (
          <Bloque titulo="Cobranza" accion={<IrTabla onClick={() => verEnTabla('cobranza')} texto="Ver en Cobranza →" />}>
            {!cobranza ? <p className="res-vacio">Cargando…</p> : (
              <div className="res-cobranza">
                <div><small>Honorarios mensuales</small><b>{honorarios ? formatoPesos(honorarios) : '—'}</b></div>
                <div>
                  <small>Debe</small>
                  {debe.length ? <b className="res-rojo">{formatoPesos(totalDebe)} · {debe.length} {debe.length === 1 ? 'mes' : 'meses'}</b> : <b>Al corriente</b>}
                </div>
                <div><small>Último pago</small><b>{ultimoPago ? `${formatoPesos(ultimoPago.monto)} · ${fechaCorta(ultimoPago.fecha, new Date(0))}` : '—'}</b></div>
                {debe.length > 0 && <p className="res-detalle">Meses: {debe.map(m => nombreMesAnio(m.mes, m.anio)).join(', ')}</p>}
              </div>
            )}
          </Bloque>
        )}

        {notas}

        <Bloque titulo="Documentos recientes" accion={<IrTabla onClick={irDocumentos} texto="Ver todos →" />}>
          {documentos === null ? <p className="res-vacio">Cargando…</p> : documentos.length === 0 ? (
            <p className="res-vacio">Sin documentos en {periodo.anio}.</p>
          ) : (
            <ul className="res-docs">
              {documentos.slice(0, 5).map(a => (
                <li key={a.id}>
                  <button type="button" className="doc-nombre" onClick={() => api.abrirArchivo(a)}>
                    <FileText size={14} aria-hidden="true" /><span>{a.nombre_archivo}</span>
                  </button>
                  <span className="doc-meta">{fechaCorta(a.fecha_subida, hoy)}</span>
                </li>
              ))}
            </ul>
          )}
        </Bloque>
      </aside>
    </div>
  );
}
